import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  Box,
  Switch,
  TextField,
  Modal,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useForm } from 'react-hook-form';
import { useDirtyTracker } from '../../../../context/UpdateContext';
import { useTranslation } from 'react-i18next';
import { isRoomAllowedForLesson } from '../../../../constants/roomTypes';
import { useToast } from 'use-toast-mui';
import QueryString from 'qs';
import moment from 'moment';

import CommonModalButton from '../../../../form-components/CommonModalButton';
import CommonSelect from '../../../../form-components/CommonSelect';
import { FormInputDate } from '../../../../form-components/FormInputDate';
import { FormInputDropdown } from '../../../../form-components/FormInputDropdown';
import { FormInputText } from '../../../../form-components/FormInputText';
import { FormInputTextArea } from '../../../../form-components/FormInputTextArea';
import CalendarIcon from '../../../CalendarIcon';
import { createLesson, updateLesson, getCourseClassTimeMembers } from '../../../../api/lesson';
import { withOverlapConfirm } from '../../../../utils/overlapConfirm';
import { getTicketsByType } from '../../../../api/ticket';
import { getMemberByTicket } from '../../../../api/member-management';
import { errorMessages, validateDateRange } from '../../../../utils/methods';
import { findDuplicateScheduleRowIndex, findOverlappingScheduleRowIndices } from '../../../../utils/scheduleValidation';

import AutoPublishCloseModal, {
  OPEN_SCHEDULE_TYPE, toOpenScheduleSettings, toOpenSchedulePayload, formatOpenSchedule,
} from '../../groupLesson/groupLessonTemplate/AutoPublishCloseModal';
import { useScheduleMoveConfirm } from '../../privateLesson/ScheduleMoveConfirm';
import TicketSelectModal from '../../groupLesson/groupLessonTemplate/TicketSelectModal';
import TemplateSelectModal from '../../groupLesson/groupLessonTemplate/TemplateSelectModal';
import { getGroupLessonTemplates, getGroupLessonTemplateById } from '../../../../api/lesson';

import {
  ScheduleCreateContainer,
  ModalHeader,
  ModalHeaderTitle,
  ScheduleCreateContent,
  SectionTitle,
  SettingGrid,
  SettingColumn,
  FieldGroup,
  ScheduleSection,
  ScheduleRow,
  ScheduleRowLabel,
  ScheduleActionButton,
  SettingButton,
  StatusChip,
  ModalFooter,
} from '../../groupLesson/groupLessonTemplate/GroupLessonTemplateStyled';

import {
  ReservationWarningTitle,
  ReservationWarningSub,
} from './GroupLessonEditModalStyled';

// ============================================
// Constants
// ============================================
const DAY_OF_WEEK_TO_NUMBER = {
  SUNDAY: 0, MONDAY: 1, TUESDAY: 2, WEDNESDAY: 3,
  THURSDAY: 4, FRIDAY: 5, SATURDAY: 6,
};

const DAY_NUMBER_TO_NAME = {
  0: 'SUNDAY', 1: 'MONDAY', 2: 'TUESDAY', 3: 'WEDNESDAY',
  4: 'THURSDAY', 5: 'FRIDAY', 6: 'SATURDAY',
};

const DAY_NUMBER_TO_LABEL = {
  0: '일요일', 1: '월요일', 2: '화요일', 3: '수요일',
  4: '목요일', 5: '금요일', 6: '토요일',
};

const DAY_SORT_ORDER = { 1: 0, 2: 1, 3: 2, 4: 3, 5: 4, 6: 5, 0: 6 };

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  value: String(i).padStart(2, '0'),
  label: String(i).padStart(2, '0'),
}));

const MINUTE_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  value: String(i * 5).padStart(2, '0'),
  label: String(i * 5).padStart(2, '0'),
}));

// ============================================
// Helpers
// ============================================
const getDayName = weekNumber =>
  moment().lang('en').day(weekNumber).format('dddd').toUpperCase();

const removeSeconds = time =>
  time?.split(':').slice(0, 2).join(':') || '';

const splitTime = time => {
  const [h, m] = (time || '00:00').split(':');
  return { hour: h || '00', minute: m || '00' };
};

const joinTime = (hour, minute) => `${hour}:${minute}`;

const normalizeScheduleKey = rows =>
  rows
    .filter(r => r.dayOfWeek !== undefined)
    .map(r => `${r.dayOfWeek}|${r.startTime}|${r.endTime}`)
    .sort()
    .join(',');

const LabelWithTooltip = ({ label, tooltip, required }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#555' }}>
      {label}{required && <span style={{ color: '#ef5350', marginLeft: '2px' }}>*</span>}
    </Typography>
    {tooltip && (
      <Tooltip
        title={<Typography sx={{ fontSize: '13px', lineHeight: 1.5 }}>{tooltip}</Typography>}
        arrow
        placement="top"
        componentsProps={{ tooltip: { sx: { backgroundColor: '#333', padding: '8px 14px', maxWidth: '260px', borderRadius: '6px' } } }}
      >
        <HelpOutlineIcon sx={{ fontSize: 15, color: '#bbb', cursor: 'help', '&:hover': { color: '#4AB3BC' } }} />
      </Tooltip>
    )}
  </Box>
);

// ============================================
// Main Component
// ============================================
export default function GroupLessonEditModal({
  mode = 'create',
  user,
  type = 'GROUP_LESSON',
  opens,
  setOpens,
  lessonData,
  setLessonData,
  lesson = null,
  onSuccess,
  handleDateSelect,
}) {
  const toast = useToast();
  const { t } = useTranslation();
  const isEditMode = mode === 'edit';

  // ============================================
  // Form
  // ============================================
  const getDefaultValues = () => {
    if (isEditMode && lesson) {
      return {
        ...lesson,
        students: lesson?.memberId ? { id: lesson?.memberId, name: lesson?.memberName || '' } : { id: '', name: '' },
        membersMaxCount: lesson?.membersMaxCount ?? null,
        membersWaitMaxCount: lesson?.membersWaitMaxCount ?? 0,
        openYn: lesson?.openYn || 'Y',
        courseTicketDeductionCount: lesson?.courseTicketDeductionCount || '1',
      };
    }
    return {
      periodStart: lessonData?.periodStart,
      periodEnd: moment(lessonData?.periodStart).add(6, 'days').toDate(),
      trainerId: opens?.trainerId,
      ticketId: '',
      openYn: 'Y',
      courseTicketDeductionCount: '1',
      membersWaitMaxCount: 0,
    };
  };

  const {
    control,
    setValue,
    watch,
    reset,
    trigger,
    setError,
    clearErrors,
    formState: { errors, isDirty },
  } = useForm({ defaultValues: getDefaultValues() });
  useDirtyTracker(isDirty);

  // ============================================
  // State
  // ============================================
  const [scheduleRows, setScheduleRows] = useState([]);
  const [loader, setLoader] = useState(false);
  const [tickets, setTickets] = useState(lessonData?.tickets || []);
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [studentsCounts, setStudentsCounts] = useState(1);
  const [waitStudentsCounts, setWaitStudentsCounts] = useState(0);
  const [searchResults, setSearchResults] = useState([]);
  const [classDuration, setClassDuration] = useState('');
  const [autoEndTime, setAutoEndTime] = useState(true);
  const [errorField, setErrorField] = useState(null);

  // Auto publish & close
  const [autoSettingOpen, setAutoSettingOpen] = useState(false);
  const [autoSettings, setAutoSettings] = useState({
    openScheduledYn: 'N', openScheduleType: OPEN_SCHEDULE_TYPE.BEFORE_DAYS, openDate: '', openBeforeDays: '',
    openHour: '', openMinute: '',
    closeScheduledYn: 'N', closeHour: '', closeMinCount: '',
  });

  // Template select
  const [templateSelectOpen, setTemplateSelectOpen] = useState(false);
  const [templateList, setTemplateList] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(lesson?.templateId || null);
  const [selectedTemplateName, setSelectedTemplateName] = useState(lesson?.templateName || '');
  const [templateConfirmOpen, setTemplateConfirmOpen] = useState(false);
  const [pendingTemplateId, setPendingTemplateId] = useState(null);

  // Confirmation
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [openYnConfirm, setOpenYnConfirm] = useState(null);

  // Edit mode specific
  const [formValues, setFormValues] = useState(null);
  const [lessonUpdateType] = useState('one');
  const [reservationWarning, setReservationWarning] = useState(null);
  const originalScheduleKeyRef = useRef(null);
  const targetSlotIndexRef = useRef(null);
  // 'one' 대상 슬롯의 원래 날짜·시간 — 날짜·시간 변경 확인(출결된 수업 동의·지난 시간 출결·푸시)에 쓴다
  const originalSlotRef = useRef(null);
  const moveConfirm = useScheduleMoveConfirm(user);
  const initialLoadRef = useRef(false);

  // ============================================
  // Computed
  // ============================================
  const trainerId = watch('trainerId');
  const firstDate = isEditMode ? watch('registrationStartDate') : watch('periodStart');
  const isOpen = isEditMode ? opens.open4 : opens.open;

  const selectedTicketIds = watch(isEditMode ? 'tickets' : 'ticketId') || [];
  const selectedTicketCount = selectedTicketIds.length;

  const trainerOptions = (lessonData?.trainers || []).map(t => ({
    value: t.trainerId,
    label: t.trainerName,
  }));

  // 강의실은 레슨구분에 맞는 유형만 고를 수 있다. 이미 행에 잡혀 있는 강의실은 유형이 달라도 남긴다.
  const selectedRoomIds = useMemo(
    () => (scheduleRows || []).map(row => row.roomId).filter(Boolean),
    [scheduleRows],
  );
  const roomOptions = useMemo(
    () =>
      (lessonData?.rooms || [])
        .filter(r => isRoomAllowedForLesson(r, type) || selectedRoomIds.includes(r.branchExerciseRoomId))
        .map(r => ({
          value: r.branchExerciseRoomId,
          label: r.branchExerciseRoomName,
        })),
    [lessonData?.rooms, type, selectedRoomIds],
  );

  // 빈 행에 자동으로 채울 강의실 — 기본 강의실이 있으면 그곳, 없으면 고를 수 있는 곳이 하나뿐일 때 그곳.
  const autoRoomId = useMemo(() => {
    const selectable = (lessonData?.rooms || []).filter(r =>
      roomOptions.some(option => option.value === r.branchExerciseRoomId),
    );
    const defaultRoom = selectable.find(r => r.defaultForGroupYn === 'Y');
    if (defaultRoom) return defaultRoom.branchExerciseRoomId;
    return roomOptions.length === 1 ? roomOptions[0].value : null;
  }, [lessonData?.rooms, roomOptions]);

  useEffect(() => {
    if (type === 'ONLINE_LESSON' || autoRoomId == null) return;
    setScheduleRows(prev =>
      prev.some(row => !row.roomId) ? prev.map(row => (row.roomId ? row : { ...row, roomId: autoRoomId })) : prev,
    );
  }, [autoRoomId, type, scheduleRows]);

  const handleOpenTemplateSelect = async () => {
    try {
      const data = await getGroupLessonTemplates(user.branch.branchId, user.token, 1, 100);
      setTemplateList(data?.content || data || []);
      setTemplateSelectOpen(true);
    } catch (error) {
      toast.error('그룹수업 목록을 불러올 수 없습니다');
    }
  };

  const handleTemplateSelect = (templateId) => {
    if (!templateId) {
      // 선택 해제
      setSelectedTemplateId(null);
      setSelectedTemplateName('');
      return;
    }
    const tpl = templateList.find(t => t.id === templateId);
    setPendingTemplateId(templateId);
    setSelectedTemplateName(tpl?.templateName || '');
    setTemplateConfirmOpen(true);
  };

  const handleTemplateConfirm = async () => {
    setTemplateConfirmOpen(false);
    setSelectedTemplateId(pendingTemplateId);
    try {
      const tpl = await getGroupLessonTemplateById(pendingTemplateId, user.token);
      if (!tpl) return;
      if (tpl.className) setValue('className', tpl.className);
      if (tpl.membersMaxCount !== undefined) setValue('membersMaxCount', tpl.membersMaxCount);
      if (tpl.membersWaitMaxCount !== undefined) setValue('membersWaitMaxCount', tpl.membersWaitMaxCount);
      if (tpl.ticketDeductionCount) setValue('courseTicketDeductionCount', tpl.ticketDeductionCount);
      if (tpl.ticketIds?.length > 0) {
        setValue('tickets', tpl.ticketIds);
      }
    } catch (error) {
      toast.error('그룹수업 정보를 불러올 수 없습니다');
    }
    setPendingTemplateId(null);
  };

  const handleTemplateCancelConfirm = () => {
    setTemplateConfirmOpen(false);
    setPendingTemplateId(null);
    setSelectedTemplateName(
      templateList.find(t => t.id === selectedTemplateId)?.templateName || lesson?.templateName || ''
    );
  };

  // ============================================
  // API Calls
  // ============================================
  const fetchTickets = async () => {
    try {
      const queryString = QueryString.stringify({
        trainerId: type === 'PRIVATE_LESSON' ? trainerId : null,
        branchId: user.branch.branchId,
        courseClassType: type,
      });
      const ticketList = await getTicketsByType(queryString, user.token);
      setTickets(ticketList);
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  const fetchMembers = async () => {
    try {
      const queryString = tickets.map(ticket => `courseTickets=${ticket?.courseTicketId}`).join('&');
      const data = await getMemberByTicket(user.token, queryString);
      setSearchResults(
        data.map(item => ({
          id: item.memberId,
          name: item.memberName,
          courseTicketId: item?.courseTicketId,
          courseTicketName: item?.courseTicketName,
        })),
      );
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  // ============================================
  // Handlers
  // ============================================
  const handleClose = () => {
    originalScheduleKeyRef.current = null;
    targetSlotIndexRef.current = null;
    initialLoadRef.current = false;
    setReservationWarning(null);
    setConfirmOpen(false);
    setErrorField(null);
    if (isEditMode) {
      setOpens({ ...opens, openMenu: false, openMenu1: false, open4: false, update: true });
    } else {
      setOpens({ ...opens, open: false, update: true });
    }
  };

  // Schedule row handlers
  const addScheduleRow = index => {
    const currentRow = scheduleRows[index];
    const duration = Number(classDuration) || 50;
    const [h, m] = (currentRow?.startTime || '00:00').split(':').map(Number);
    const newStartTotal = h * 60 + m + 60;
    const newStartTime = `${String(Math.floor(newStartTotal / 60) % 24).padStart(2, '0')}:${String(newStartTotal % 60).padStart(2, '0')}`;
    const newEndTotal = newStartTotal + duration;
    const newEndTime = `${String(Math.floor(newEndTotal / 60) % 24).padStart(2, '0')}:${String(newEndTotal % 60).padStart(2, '0')}`;

    setScheduleRows(prev => [
      ...prev.slice(0, index + 1),
      {
        id: Date.now(),
        dayOfWeek: currentRow?.dayOfWeek,
        startTime: newStartTime,
        endTime: newEndTime,
        openYn: autoSettings.openScheduledYn === 'Y' ? 'N' : (currentRow?.openYn || 'Y'),
        trainerId: currentRow?.trainerId || '',
        roomId: currentRow?.roomId || '',
      },
      ...prev.slice(index + 1),
    ]);
  };

  const removeScheduleRow = index => {
    if (scheduleRows.length <= 1) return;
    setScheduleRows(prev => prev.filter((_, i) => i !== index));
  };

  const updateScheduleRow = (index, field, value) => {
    setScheduleRows(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      if (autoEndTime && classDuration) {
        const duration = Number(classDuration);
        if (field === 'startTime') {
          const [sH, sM] = value.split(':').map(Number);
          const newEnd = sH * 60 + sM + duration;
          updated[index].endTime = `${String(Math.floor(newEnd / 60) % 24).padStart(2, '0')}:${String(newEnd % 60).padStart(2, '0')}`;
        } else if (field === 'endTime') {
          const [eH, eM] = value.split(':').map(Number);
          const newStart = eH * 60 + eM - duration;
          if (newStart >= 0) {
            updated[index].startTime = `${String(Math.floor(newStart / 60) % 24).padStart(2, '0')}:${String(newStart % 60).padStart(2, '0')}`;
          }
        }
      }
      return updated;
    });
  };

  // Ticket handlers
  const handleTicketIdsChange = newIds => {
    const fieldName = isEditMode ? 'tickets' : 'ticketId';
    setValue(fieldName, newIds);
    clearErrors(fieldName);
    if (errorField === 'tickets') setErrorField(null);

    // selectedTickets state 동기화 (edit submit에서 ticketType 구분에 필요)
    const ticketMap = {};
    tickets.forEach(t => { ticketMap[t.courseTicketId] = t; });
    selectedTickets.forEach(t => { if (!ticketMap[t.courseTicketId]) ticketMap[t.courseTicketId] = t; });
    setSelectedTickets(
      newIds.map(id => ticketMap[id]).filter(Boolean).map(t => ({
        courseTicketId: t.courseTicketId,
        courseTicketName: t.courseTicketName,
        ticketType: t.ticketType || 'COURSE_TICKET',
      })),
    );
  };

  // ============================================
  // Validation & Submit
  // ============================================
  const validateForm = async () => {
    const isValid = await trigger();
    const values = watch();
    let hasError = false;

    if (!values.className?.trim()) {
      setErrorField('className');
      toast.error('수업명을 입력해주세요');
      hasError = true;
    }

    if (type === 'GROUP_LESSON') {
      const ticketField = isEditMode ? 'tickets' : 'ticketId';
      if (!values[ticketField] || values[ticketField].length === 0) {
        setErrorField('tickets');
        toast.error('수강권을 선택해주세요');
        hasError = true;
      }
      if (!values.courseTicketDeductionCount || Number(values.courseTicketDeductionCount) <= 0) {
        setErrorField('ticketDeductionCount');
        toast.error('수강권 차감 횟수를 입력해주세요');
        hasError = true;
      }
      if (!classDuration || Number(classDuration) <= 0) {
        setErrorField('classDuration');
        toast.error('수업 시간을 입력해주세요');
        hasError = true;
      } else if (Number(classDuration) % 5 !== 0) {
        setErrorField('classDuration');
        toast.error('수업 시간은 5분 단위로 입력해주세요');
        hasError = true;
      }
      const maxCount = isEditMode ? values.membersMaxCount : studentsCounts;
      if (!maxCount || Number(maxCount) <= 0) {
        setErrorField('membersMaxCount');
        toast.error('수업 정원을 입력해주세요');
        hasError = true;
      }
      // 수정 모드: 현재 예약/대기 인원보다 적은 정원 설정 방지
      if (isEditMode && !hasError) {
        const currentReserved = (lessonData?.eventData?.reservedMemberCount || 0) + (lessonData?.eventData?.attendedMemberCount || 0);
        const currentWaiting = lessonData?.eventData?.waitingMemberCount || 0;
        if (Number(maxCount) < currentReserved) {
          setErrorField('membersMaxCount');
          toast.error(`현재 예약 인원(${currentReserved}명)보다 적은 정원을 설정할 수 없습니다`);
          hasError = true;
        }
        const waitMax = Number(values.membersWaitMaxCount || 0);
        if (waitMax > 0 && waitMax < currentWaiting) {
          setErrorField('membersWaitMaxCount');
          toast.error(`현재 대기 인원(${currentWaiting}명)보다 적은 대기 정원을 설정할 수 없습니다`);
          hasError = true;
        }
      }
    }

    const roomIdValue = isEditMode ? values.branchExerciseRoomId : values.roomId;
    if (type !== 'ONLINE_LESSON' && !opens.roomId && !roomIdValue) {
      setErrorField('room');
      toast.error('강의실을 선택해주세요');
      hasError = true;
    }

    if (!opens.trainerId) {
      const trainerField = isEditMode ? values.trainerId : values.trainerIdList;
      if (!trainerField || (Array.isArray(trainerField) && trainerField.length === 0)) {
        setErrorField('trainer');
        toast.error('담당자를 선택해주세요');
        hasError = true;
      }
    }

    // 시간 검증
    const invalidTimeIdx = scheduleRows.findIndex(row => row.startTime >= row.endTime);
    if (invalidTimeIdx >= 0) {
      const row = scheduleRows[invalidTimeIdx];
      setErrorField(`time_${invalidTimeIdx}`);
      toast.error(`${DAY_NUMBER_TO_LABEL[row.dayOfWeek] || ''} ${row.startTime}~${row.endTime} 스케줄의 시작시간이 종료시간보다 같거나 늦습니다`);
      hasError = true;
    }

    // 요일 검증
    const hasSlotWithoutDay = scheduleRows.some(r => r.dayOfWeek === undefined);
    if (hasSlotWithoutDay) {
      toast.error('각 수업 시간의 요일을 선택해주세요');
      hasError = true;
    }

    // 중복 검증 (요일+시간+강의실+강사가 모두 같을 때만 중복)
    const duplicateIdx = findDuplicateScheduleRowIndex(scheduleRows);
    if (duplicateIdx >= 0) {
      const row = scheduleRows[duplicateIdx];
      setErrorField(`time_${duplicateIdx}`);
      toast.error(`${DAY_NUMBER_TO_LABEL[row.dayOfWeek] || ''} ${row.startTime}~${row.endTime} 스케줄이 중복됩니다`);
      hasError = true;
    }

    // 같은 요일+같은 강사 내 시간 겹침 검사 (한 강사가 동시에 두 수업 불가, 강의실은 무관)
    if (!hasError) {
      const overlappingPair = findOverlappingScheduleRowIndices(scheduleRows);
      if (overlappingPair) {
        const [i, j] = overlappingPair;
        const a = scheduleRows[i];
        const b = scheduleRows[j];
        setErrorField(`time_${j}`);
        toast.error(`${DAY_NUMBER_TO_LABEL[a.dayOfWeek] || ''} ${a.startTime}~${a.endTime}과 ${b.startTime}~${b.endTime} 스케줄의 시간이 겹칩니다`);
        hasError = true;
      }
    }

    if (!isEditMode) {
      const periodStart = values.periodStart;
      const periodEnd = values.periodEnd;
      if (!periodStart || !periodEnd) {
        setErrorField('period');
        toast.error('등록 기간을 설정해주세요');
        hasError = true;
      } else if (!validateDateRange(periodStart, periodEnd)) {
        setErrorField('period');
        toast.error('기간을 다시 확인해주세요');
        hasError = true;
      }
    }

    return !hasError && isValid;
  };

  const buildClassTimeInfoList = (rows = scheduleRows) =>
    rows
      .filter(r => r.dayOfWeek !== undefined)
      .map(r => ({
        dayOfWeek: DAY_NUMBER_TO_NAME[r.dayOfWeek],
        startTime: r.startTime,
        endTime: r.endTime,
        openYn: r.openYn || 'Y',
        trainerId: r.trainerId || null,
        exerciseRoomId: r.roomId || null,
      }));

  const handleFormSubmit = async () => {
    const isValid = await validateForm();
    if (!isValid) return;
    setErrorField(null);

    if (isEditMode) {
      // formValues 준비
      const values = watch();
      const { tickets: ticketsList, trainers, currentDayInfo, rooms, ...filterDates } = lessonData;
      const { classRoom, students, ...filterValues } = values;

      if (!validateDateRange(
        lesson?.registrationStartDate || filterValues.registrationStartDate,
        lesson?.registrationEndDate || filterValues.registrationEndDate,
      )) {
        toast.error('기간을 다시 확인해주세요');
        return;
      }

      const selectedTrialTicketIds =
        selectedTickets?.filter(t => t?.ticketType === 'TRIAL_TICKET').map(t => t?.courseTicketId) || [];
      const selectedRegularTicketIds =
        selectedTickets?.filter(t => t?.ticketType !== 'TRIAL_TICKET').map(t => t?.courseTicketId) || [];

      const idx = targetSlotIndexRef.current;
      const target = idx != null && idx >= 0 ? scheduleRows[idx] : null;

      setFormValues({
        courseClassId: lesson?.courseClassId || lessonData?.eventData?.courseClassId,
        courseClassTimeId: lessonData?.eventData?.courseClassTimeId || null,
        branchId: user.branch?.branchId,
        courseClassType: type,
        className: filterValues.className,
        registrationStartDate: lesson?.registrationStartDate || moment(filterValues.registrationStartDate).format('YYYY-MM-DD'),
        registrationEndDate: lesson?.registrationEndDate || moment(filterValues.registrationEndDate).format('YYYY-MM-DD'),
        classTimeInfoList: buildClassTimeInfoList(),
        membersMaxCount: type === 'GROUP_LESSON' && filterValues.membersMaxCount ? Number(filterValues.membersMaxCount) : null,
        membersWaitMaxCount: type === 'GROUP_LESSON' && filterValues.membersWaitMaxCount ? Number(filterValues.membersWaitMaxCount) : null,
        memberId: type === 'GROUP_LESSON' ? null : students?.id || null,
        exerciseRoomId: target?.roomId || opens.roomId || filterValues.branchExerciseRoomId,
        trainerId: target?.trainerId || opens.trainerId || filterValues.trainerId,
        tickets: selectedRegularTicketIds,
        trialTickets: selectedTrialTicketIds,
        allTicketsSelectedYn: selectedTickets.length === tickets.length && tickets.length > 0 ? 'Y' : 'N',
        openYn: autoSettings.openScheduledYn === 'Y' ? 'N' : (scheduleRows[0]?.openYn || 'Y'),
        openScheduledYn: autoSettings.openScheduledYn,
        ...toOpenSchedulePayload(autoSettings),
        closeScheduledYn: autoSettings.closeScheduledYn,
        closeScheduledMinCount: autoSettings.closeMinCount ? Number(autoSettings.closeMinCount) : null,
        closeScheduledTime: autoSettings.closeHour || null,
        courseTicketDeductionCount: filterValues.courseTicketDeductionCount ? Number(filterValues.courseTicketDeductionCount) : 1,
        memo: filterValues.memo || null,
        templateId: selectedTemplateId,
      });
    }

    setConfirmOpen(true);
  };

  const detectTimeChange = () =>
    originalScheduleKeyRef.current !== null && originalScheduleKeyRef.current !== normalizeScheduleKey(scheduleRows);

  const buildUpdatePayload = () => {
    const idx = targetSlotIndexRef.current;
    const target = idx != null && idx >= 0 ? scheduleRows[idx] : null;

    const base = { ...formValues };

    if (target && target.dayOfWeek !== undefined) {
      base.classTimeInfoList = [{
        dayOfWeek: DAY_NUMBER_TO_NAME[target.dayOfWeek],
        startTime: target.startTime,
        endTime: target.endTime,
        openYn: target.openYn || 'Y',
        trainerId: target.trainerId || null,
        exerciseRoomId: target.roomId || null,
      }];
    }

    return base;
  };

  // 수정 저장 — 날짜·시간이 바뀌면 확인 단계를 거친다. 취소하면 false.
  // 그룹 수정은 요일만 바꾸므로 새 날짜는 백엔드와 같이 원래 수업일이 속한 주(월요일 시작)의 그 요일이다.
  const submitUpdate = async () => {
    const payload = buildUpdatePayload();
    const from = originalSlotRef.current;
    const timeInfo = payload.classTimeInfoList?.[0];
    let decision = { pastAttendanceStatus: undefined, notify: false, memberIds: [] };
    if (from && timeInfo) {
      const dayNumber = DAY_OF_WEEK_TO_NUMBER[timeInfo.dayOfWeek];
      const toDate = moment(from.date)
        .isoWeekday(1)
        .add(dayNumber === 0 ? 6 : dayNumber - 1, 'days')
        .format('YYYY-MM-DD');
      decision = await moveConfirm.ask({
        courseClassId: payload.courseClassTimeId || payload.courseClassId,
        from,
        to: { date: toDate, startTime: timeInfo.startTime, endTime: timeInfo.endTime },
      });
      if (!decision) return false;
      await withOverlapConfirm(force =>
        updateLesson({ ...payload, pastAttendanceStatus: decision.pastAttendanceStatus }, user.token, force),
      );
      await moveConfirm.notify(decision, {
        branchId: user.branch?.branchId,
        trainerName: lessonData?.trainers?.find(trainer => trainer.trainerId === (timeInfo.trainerId || payload.trainerId))
          ?.trainerName,
        className: payload.className,
        date: toDate,
        startTime: timeInfo.startTime,
      });
      return true;
    }
    await withOverlapConfirm(force => updateLesson(payload, user.token, force));
    return true;
  };

  const handleConfirmSubmit = async () => {
    setLoader(true);
    try {
      if (isEditMode) {
        // 시간 변경 시 예약자 확인
        if (detectTimeChange()) {
          const targetId = lessonUpdateType === 'one' ? formValues?.courseClassTimeId : null;
          let warningTotal = null;

          if (targetId) {
            const members = await getCourseClassTimeMembers(targetId, user.token);
            warningTotal = (members ?? []).filter(m => m.status === 'RESERVED').length;
            if (warningTotal === 0) {
              if (!(await submitUpdate())) {
                setLoader(false);
                return;
              }
              toast.success(t('Class updated successfully'));
              setConfirmOpen(false);
              handleClose();
              onSuccess?.(formValues?.courseClassTimeId || formValues?.courseClassId);
              return;
            }
          }

          setConfirmOpen(false);
          setReservationWarning({ total: warningTotal });
          setLoader(false);
          return;
        }

        if (!(await submitUpdate())) {
          setLoader(false);
          return;
        }
        toast.success(t('Class updated successfully'));
        setConfirmOpen(false);
        handleClose();
        onSuccess?.(formValues?.courseClassTimeId || formValues?.courseClassId);
      } else {
        // Create
        const values = watch();
        const { tickets: ticketsList, trainers, currentDayInfo, rooms, ...filterDates } = lessonData;
        const { classRoom, students, ticketId, ...filterValues } = values;

        if (!validateDateRange(filterValues.periodStart, filterValues.periodEnd)) {
          toast.error('기간을 다시 확인해주세요');
          setLoader(false);
          return;
        }

        const selectedIds = ticketId || [];
        const selectedTrialTicketIds = tickets
          .filter(t => selectedIds.includes(t.courseTicketId) && t.ticketType === 'TRIAL_TICKET')
          .map(t => t.courseTicketId);
        const selectedRegularTicketIds = tickets
          .filter(t => selectedIds.includes(t.courseTicketId) && t.ticketType !== 'TRIAL_TICKET')
          .map(t => t.courseTicketId);

        await withOverlapConfirm(force => createLesson(
          {
            ...filterDates,
            ...filterValues,
            className: filterValues.className?.trim(),
            registrationStartDate: moment(filterValues.periodStart).format('YYYY-MM-DD'),
            registrationEndDate: moment(filterValues.periodEnd).format('YYYY-MM-DD'),
            classTimeInfoList: buildClassTimeInfoList(),
            memberId: type === 'GROUP_LESSON' ? null : students?.id || null,
            membersMaxCount: type === 'GROUP_LESSON' ? Number(studentsCounts) : null,
            membersWaitMaxCount: Number(waitStudentsCounts),
            exerciseRoomId: opens.roomId || filterValues.roomId,
            branchId: user.branch?.branchId,
            courseClassType: type,
            trainerId: opens.trainerId || filterValues.trainerIdList,
            tickets: selectedRegularTicketIds,
            ticketId: selectedRegularTicketIds,
            trialTickets: selectedTrialTicketIds,
            allTicketsSelectedYn: selectedIds.length === tickets.length && tickets.length > 0 ? 'Y' : 'N',
            openScheduledYn: autoSettings.openScheduledYn,
            ...toOpenSchedulePayload(autoSettings),
            closeScheduledYn: autoSettings.closeScheduledYn,
            closeScheduledMinCount: autoSettings.closeMinCount ? Number(autoSettings.closeMinCount) : null,
            closeScheduledTime: autoSettings.closeHour || null,
            courseTicketDeductionCount: filterValues.courseTicketDeductionCount ? Number(filterValues.courseTicketDeductionCount) : 1,
            memo: filterValues.memo,
          },
          user.token,
          force,
        ));

        toast.success(t('Class created successfully'));
        setConfirmOpen(false);
        onSuccess?.();
        handleClose();
      }
    } catch (error) {
      // 겹침 확인창에서 취소했으면 안내 없이 모달을 유지한다
      if (!error?.silent) toast.error(errorMessages(error));
    }
    setLoader(false);
  };

  const handleProceedDespiteReservation = async () => {
    setLoader(true);
    try {
      if (!(await submitUpdate())) return;
      toast.success(t('Class updated successfully'));
      setReservationWarning(null);
      handleClose();
      onSuccess?.(formValues?.courseClassTimeId || formValues?.courseClassId);
    } catch (error) {
      if (!error?.silent) toast.error(errorMessages(error));
    } finally {
      setLoader(false);
    }
  };

  const handleCancelReservationWarning = () => {
    setReservationWarning(null);
    setConfirmOpen(true);
  };

  // ============================================
  // Effects
  // ============================================
  useEffect(() => {
    fetchTickets();
    if (!isEditMode) {
      setValue('className', lessonData?.trainers?.find(t => t.trainerId === trainerId)?.trainerName);
    }
  }, [trainerId]);

  // Edit mode: lesson 데이터 로드
  useEffect(() => {
    if (isEditMode && lesson) {
      const regularTickets = lesson?.tickets?.map(t => ({
        courseTicketId: t?.ticketId, courseTicketName: t?.ticketName, ticketType: 'COURSE_TICKET',
      })) || [];
      const trialTickets = lesson?.trialTicketList?.map(t => ({
        courseTicketId: t?.trialTicketId, courseTicketName: t?.trialTicketName, ticketType: 'TRIAL_TICKET',
      })) || [];
      setSelectedTickets([...regularTickets, ...trialTickets]);

      reset(getDefaultValues());

      const ticketIds = lesson.tickets?.map(t => t.ticketId) || [];
      const trialTicketIds = lesson.trialTicketList?.map(t => t.trialTicketId) || [];
      setValue('tickets', [...ticketIds, ...trialTicketIds]);

      // classDuration 초기화: 첫 번째 classTime의 시작~종료 차이로 계산
      if (lesson.classTimes?.length > 0) {
        const firstCt = lesson.classTimes[0];
        const [sh, sm] = (firstCt.startTime || '00:00').split(':').map(Number);
        const [eh, em] = (firstCt.endTime || '00:50').split(':').map(Number);
        setClassDuration(String((eh * 60 + em) - (sh * 60 + sm)));
      }
      initialLoadRef.current = true;
      setAutoEndTime(true);

      // Schedule rows 초기화
      if (lesson.classTimes) {
        const flatRows = [];
        const seen = new Set();
        lesson.classTimes.forEach(ct => {
          const dayNum = DAY_OF_WEEK_TO_NUMBER[ct.dayOfWeek];
          const startTime = removeSeconds(ct.startTime);
          const endTime = removeSeconds(ct.endTime);
          const key = `${dayNum}_${startTime}_${endTime}`;
          if (!seen.has(key)) {
            seen.add(key);
            flatRows.push({
              id: ct.courseClassTimeId || Date.now() + Math.random(),
              dayOfWeek: dayNum,
              startTime,
              endTime,
              openYn: lesson.openYn || 'Y',
              trainerId: lesson.trainerId || '',
              roomId: lesson.branchExerciseRoomId || lesson.exerciseRoomId || '',
            });
          }
        });
        flatRows.sort((a, b) => (DAY_SORT_ORDER[a.dayOfWeek] ?? 99) - (DAY_SORT_ORDER[b.dayOfWeek] ?? 99));
        setScheduleRows(flatRows);

        if (originalScheduleKeyRef.current === null) {
          originalScheduleKeyRef.current = normalizeScheduleKey(flatRows);
        }

        // 'one' 대상 슬롯 인덱스
        const targetCt = lessonData?.eventData?.courseClassTimeId
          ? lesson.classTimes.find(ct => ct.courseClassTimeId === lessonData.eventData.courseClassTimeId)
          : null;
        targetSlotIndexRef.current = targetCt
          ? flatRows.findIndex(r =>
              r.dayOfWeek === DAY_OF_WEEK_TO_NUMBER[targetCt.dayOfWeek] &&
              r.startTime === removeSeconds(targetCt.startTime),
            )
          : null;
        // 수업일만 쓴다 — 등록 시작일은 반복 등록분에서 수업일과 달라 확인 단계가 날짜 변경을 오판한다
        const originalDate = lessonData?.eventData?.classDate;
        originalSlotRef.current =
          targetCt && originalDate
            ? {
                date: moment(originalDate).format('YYYY-MM-DD'),
                startTime: removeSeconds(targetCt.startTime),
                endTime: removeSeconds(targetCt.endTime),
              }
            : null;
      }

      // Auto settings 초기화
      const isOpenSet = lesson?.openScheduledYn === 'Y';
      // openScheduledTime: "09:30" 또는 "09" 형식
      const rawOpenTime = lesson?.openScheduledTime || '';
      const [rawOpenHour, rawOpenMinute] = rawOpenTime.includes(':')
        ? rawOpenTime.split(':')
        : [rawOpenTime, ''];
      // closeScheduledTime: 패딩 제거 ("02" → "2", CLOSE_HOUR_OPTIONS 포맷 일치)
      const rawCloseHour = lesson?.closeScheduledTime || '';
      const parsedCloseHour = rawCloseHour ? String(parseInt(rawCloseHour, 10)) : '';

      setAutoSettings({
        openScheduledYn: lesson?.openScheduledYn || 'N',
        ...toOpenScheduleSettings(lesson),
        openHour: rawOpenHour || '',
        openMinute: rawOpenMinute || (isOpenSet && rawOpenHour ? '00' : ''),
        closeScheduledYn: lesson?.closeScheduledYn || 'N',
        closeHour: parsedCloseHour,
        closeMinCount: lesson?.closeScheduledMinCount?.toString() || '',
      });
    }
  }, [lesson]);

  // Create mode: 초기 스케줄 행
  useEffect(() => {
    if (!isEditMode && isOpen) {
      const dayOfWeek = lessonData?.currentDayInfo?.week;
      const startTime = removeSeconds(lessonData?.currentDayInfo?.timeStart) || '00:00';
      const endTime = removeSeconds(lessonData?.currentDayInfo?.timeEnd) || '00:50';
      // classDuration 계산
      const [sh, sm] = startTime.split(':').map(Number);
      const [eh, em] = endTime.split(':').map(Number);
      setClassDuration(String((eh * 60 + em) - (sh * 60 + sm)) || '50');
      setAutoEndTime(true);
      setScheduleRows([{
        id: Date.now(),
        dayOfWeek,
        startTime,
        endTime,
        openYn: 'Y',
        trainerId: opens?.trainerId || '',
        roomId: opens?.roomId || '',
      }]);
      setAutoSettings({
        openScheduledYn: 'N', openScheduleType: OPEN_SCHEDULE_TYPE.BEFORE_DAYS, openDate: '', openBeforeDays: '',
    openHour: '', openMinute: '',
        closeScheduledYn: 'N', closeHour: '', closeMinCount: '',
      });
      setErrorField(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (lessonData?.type === 0 && tickets.length > 0) {
      fetchMembers();
    }
  }, [lessonData?.type, tickets, selectedTickets]);

  useEffect(() => {
    if (!isEditMode && firstDate) {
      setValue('periodEnd', moment(firstDate).add(6, 'days').toDate());
    }
  }, [firstDate]);

  // classDuration 변경 시 종료시간 자동 계산
  useEffect(() => {
    // 초기 로드 시에는 DB에서 가져온 종료시간을 유지
    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      return;
    }
    if (autoEndTime && classDuration) {
      setScheduleRows(prev =>
        prev.map(row => {
          if (!row.startTime) return row;
          const [h, m] = row.startTime.split(':').map(Number);
          const endTotal = h * 60 + m + Number(classDuration);
          const eH = String(Math.floor(endTotal / 60) % 24).padStart(2, '0');
          const eM = String(endTotal % 60).padStart(2, '0');
          return { ...row, endTime: `${eH}:${eM}` };
        }),
      );
    }
  }, [classDuration, autoEndTime]);

  // ============================================
  // Render
  // ============================================
  const periodStart = isEditMode ? lesson?.registrationStartDate : watch('periodStart');
  const periodEnd = isEditMode ? lesson?.registrationEndDate : watch('periodEnd');

  // 스케줄 행을 요일별로 그룹핑
  const dayGroups = useMemo(() => {
    const groups = [];
    let currentDay = null;
    let currentGroup = [];

    scheduleRows.forEach((row, index) => {
      if (row.dayOfWeek !== currentDay) {
        if (currentGroup.length > 0) groups.push({ day: currentDay, rows: currentGroup });
        currentDay = row.dayOfWeek;
        currentGroup = [{ row, index }];
      } else {
        currentGroup.push({ row, index });
      }
    });
    if (currentGroup.length > 0) groups.push({ day: currentDay, rows: currentGroup });
    return groups;
  }, [scheduleRows]);

  return (
    <>
      <Modal open={isOpen} onClose={handleClose} sx={{ '& :focus-visible': { outline: 'none' } }}>
        <ScheduleCreateContainer>
          <ModalHeader>
            <ModalHeaderTitle>
              {isEditMode ? '그룹레슨 스케줄 수정' : '그룹레슨 스케줄 등록'}
            </ModalHeaderTitle>
            <CloseIcon onClick={handleClose} sx={{ color: '#fff', cursor: 'pointer', '&:hover': { opacity: 0.7 } }} />
          </ModalHeader>

          <ScheduleCreateContent>
            <SectionTitle>수업 설정</SectionTitle>

            <SettingGrid>
              {/* ============================== 왼쪽 열 ============================== */}
              <SettingColumn>
                {/* 그룹수업 (수정 모드에서만 표시) */}
                {isEditMode && (
                  <FieldGroup>
                    <LabelWithTooltip label="그룹수업" tooltip="클릭하면 연결된 그룹수업을 변경할 수 있습니다" />
                    <Box
                      onClick={handleOpenTemplateSelect}
                      sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        height: '40px', px: '14px',
                        border: '1px solid #c4c4c4', borderRadius: '6px',
                        backgroundColor: '#fff', cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        '&:hover': { borderColor: '#4AB3BC', boxShadow: '0 0 0 1px rgba(74,179,188,0.1)' },
                      }}>
                      <Typography sx={{ fontSize: '14px', color: selectedTemplateName ? '#555' : '#bbb' }}>
                        {selectedTemplateName || '선택 안 함'}
                      </Typography>
                      <KeyboardArrowDownIcon sx={{ color: '#999', fontSize: 18 }} />
                    </Box>
                  </FieldGroup>
                )}

                {/* 수업명 */}
                <FieldGroup>
                  <LabelWithTooltip label="수업명" tooltip="캘린더에 표시될 수업 이름입니다" required />
                  <FormInputText
                    control={control}
                    name="className"
                    placeholder="수업명을 입력해 주세요."
                    sx={{
                      width: '100%',
                      ...(errorField === 'className' && {
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d32f2f !important', borderWidth: '2px !important' },
                      }),
                    }}
                    onChange={() => { if (errorField === 'className') setErrorField(null); }}
                  />
                </FieldGroup>

                {/* 수강권 선택 */}
                {type === 'GROUP_LESSON' && (
                  <FieldGroup>
                    <LabelWithTooltip label="수강권 선택" tooltip="이 수업에 사용할 수 있는 수강권을 선택합니다" required />
                    <Box
                      onClick={() => setTicketModalOpen(true)}
                      sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        height: '40px', px: '14px',
                        border: errorField === 'tickets' ? '2px solid #d32f2f' : '1px solid #c4c4c4',
                        borderRadius: '6px', cursor: 'pointer',
                        backgroundColor: selectedTicketCount > 0 ? '#f0f8f8' : '#fff',
                        transition: 'all 0.15s ease',
                        '&:hover': { borderColor: '#4AB3BC', boxShadow: '0 0 0 1px rgba(74,179,188,0.1)' },
                      }}
                    >
                      <Typography sx={{ fontSize: '14px', fontWeight: selectedTicketCount > 0 ? 500 : 400, color: selectedTicketCount > 0 ? '#4AB3BC' : '#999' }}>
                        {selectedTicketCount > 0 ? `${selectedTicketCount}개 수강권 선택됨` : '수강권을 선택해 주세요'}
                      </Typography>
                      <KeyboardArrowDownIcon sx={{ color: '#999', fontSize: 18 }} />
                    </Box>
                  </FieldGroup>
                )}

                {/* 강의실 - 스케줄 행으로 이동 (create mode에서 opens.roomId 있을 때만 표시) */}
                {type !== 'ONLINE_LESSON' && !isEditMode && opens.roomId && (
                  <FieldGroup>
                    <LabelWithTooltip label="강의실" tooltip="수업이 진행될 강의실" />
                    <Typography sx={{ fontSize: '14px', fontWeight: 'bold', py: '8px' }}>
                      {lessonData?.rooms?.find(r => r.branchExerciseRoomId === opens.roomId)?.branchExerciseRoomName}
                    </Typography>
                  </FieldGroup>
                )}
              </SettingColumn>

              {/* ============================== 가운데 열 ============================== */}
              <SettingColumn>
                {type === 'GROUP_LESSON' && (
                  <>
                    {/* 수업 시간 + 차감 횟수 */}
                    <FieldGroup>
                      <Box sx={{ display: 'flex', gap: '12px' }}>
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <LabelWithTooltip label="수업 시간" tooltip="수업 1회의 진행 시간 (분)" required />
                          <TextField
                            size="small"
                            fullWidth
                            placeholder="0"
                            value={classDuration}
                            onChange={e => { setClassDuration(e.target.value.replace(/[^0-9]/g, '')); if (errorField === 'classDuration') setErrorField(null); }}
                            error={errorField === 'classDuration'}
                            InputProps={{ endAdornment: <span style={{ color: '#999', fontSize: '14px' }}>분</span> }}
                          />
                        </Box>
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <LabelWithTooltip label="수강권 차감 횟수" tooltip="예약 시 수강권에서 차감되는 횟수" required />
                          <FormInputText
                            control={control}
                            name="courseTicketDeductionCount"
                            type="text"
                            placeholder="0"
                            onChange={e => setValue('courseTicketDeductionCount', e.target.value.replace(/[^0-9]/g, ''))}
                            sx={{
                              width: '100%',
                              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { display: 'none' },
                              ...(errorField === 'ticketDeductionCount' && {
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d32f2f !important', borderWidth: '2px !important' },
                              }),
                            }}
                          />
                        </Box>
                      </Box>
                    </FieldGroup>

                    {/* 정원 + 대기 정원 */}
                    <FieldGroup>
                      <Box sx={{ display: 'flex', gap: '12px' }}>
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <LabelWithTooltip label="수업 정원" tooltip="수업에 예약할 수 있는 최대 인원" required />
                          {isEditMode ? (
                            <FormInputText
                              control={control}
                              name="membersMaxCount"
                              type="text"
                              placeholder="0"
                              onChange={e => setValue('membersMaxCount', e.target.value.replace(/[^0-9]/g, ''))}
                              sx={{
                                width: '100%',
                                '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { display: 'none' },
                                ...(errorField === 'membersMaxCount' && {
                                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d32f2f !important', borderWidth: '2px !important' },
                                }),
                              }}
                            />
                          ) : (
                            <TextField
                              size="small"
                              fullWidth
                              placeholder="0"
                              value={studentsCounts}
                              onChange={e => { setStudentsCounts(e.target.value.replace(/[^0-9]/g, '')); if (errorField === 'membersMaxCount') setErrorField(null); }}
                              error={errorField === 'membersMaxCount'}
                              InputProps={{ endAdornment: <span style={{ color: '#999', fontSize: '14px' }}>명</span> }}
                            />
                          )}
                        </Box>
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <LabelWithTooltip label="대기 정원" tooltip="정원 초과 시 대기할 수 있는 최대 인원" />
                          {isEditMode ? (
                            <FormInputText
                              control={control}
                              name="membersWaitMaxCount"
                              type="text"
                              placeholder="0"
                              onChange={e => setValue('membersWaitMaxCount', e.target.value.replace(/[^0-9]/g, ''))}
                              sx={{
                                width: '100%',
                                '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { display: 'none' },
                              }}
                            />
                          ) : (
                            <TextField
                              size="small"
                              fullWidth
                              placeholder="0"
                              value={waitStudentsCounts}
                              onChange={e => setWaitStudentsCounts(e.target.value.replace(/[^0-9]/g, ''))}
                              InputProps={{ endAdornment: <span style={{ color: '#999', fontSize: '14px' }}>명</span> }}
                            />
                          )}
                        </Box>
                      </Box>
                    </FieldGroup>
                  </>
                )}

                {/* 자동 공개&폐강 */}
                <FieldGroup>
                  <LabelWithTooltip label="자동 공개&폐강" tooltip="수업 공개/폐강을 자동으로 예약하는 설정" />
                  <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <SettingButton onClick={() => setAutoSettingOpen(true)}>설정하기</SettingButton>
                    <StatusChip active={autoSettings.openScheduledYn === 'Y'}>공개</StatusChip>
                    <StatusChip active={autoSettings.closeScheduledYn === 'Y'}>폐강</StatusChip>
                  </Box>
                </FieldGroup>
              </SettingColumn>

              {/* ============================== 오른쪽 열 ============================== */}
              <SettingColumn>
                {/* 수업 기간 */}
                <FieldGroup>
                  <LabelWithTooltip label="수업 기간" tooltip="수업이 생성될 날짜 범위" required />
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    ...(errorField === 'period' && {
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d32f2f !important', borderWidth: '2px !important' },
                    }),
                  }}>
                    <FormInputDate
                      name={isEditMode ? 'registrationStartDate' : 'periodStart'}
                      control={control}
                      placeholder="시작일"
                      disabled={isEditMode}
                      components={{ OpenPickerIcon: CalendarIcon }}
                      onChange={() => { if (errorField === 'period') setErrorField(null); }}
                    />
                    <Typography>~</Typography>
                    <FormInputDate
                      name={isEditMode ? 'registrationEndDate' : 'periodEnd'}
                      control={control}
                      placeholder="종료일"
                      disabled={isEditMode}
                      minDate={firstDate ? moment(firstDate).add(6, 'days').toDate() : undefined}
                      components={{ OpenPickerIcon: CalendarIcon }}
                      onChange={() => { if (errorField === 'period') setErrorField(null); }}
                    />
                  </Box>
                </FieldGroup>

                {/* 메모 */}
                <FieldGroup>
                  <LabelWithTooltip label="메모" tooltip="관리자용 메모 (회원에게 노출되지 않음)" />
                  <FormInputTextArea
                    control={control}
                    name="memo"
                    minRows={2}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '4px',
                      height: '72px',
                      borderColor: 'rgba(133, 133, 133, 0.5)',
                    }}
                    placeholder="메모를 입력해 주세요."
                  />
                </FieldGroup>
              </SettingColumn>
            </SettingGrid>

            {/* ============================== 스케줄 설정 ============================== */}
            <ScheduleSection>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: '16px' }}>
                <SectionTitle sx={{ mb: 0 }}>스케줄 설정</SectionTitle>
              </Box>

              {dayGroups.map(group => (
                <Box key={group.day ?? 'none'} sx={{
                  border: '1px solid #e8e8e8', borderRadius: '8px', mb: '10px', overflow: 'hidden',
                }}>
                  {group.rows.map(({ row, index }, groupIdx) => (
                    <ScheduleRow key={row.id} sx={{
                      borderBottom: groupIdx < group.rows.length - 1 ? '1px dashed #e8e8e8' : 'none',
                      margin: 0, borderRadius: 0, border: 'none',
                      ...(errorField === `time_${index}` && {
                        backgroundColor: '#FEF5F5',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D32F2F !important', borderWidth: '2px !important' },
                      }),
                    }}>
                      {/* 요일 */}
                      <CommonSelect
                        value={row.dayOfWeek !== undefined ? String(row.dayOfWeek) : ''}
                        onChange={e => updateScheduleRow(index, 'dayOfWeek', Number(e.target.value))}
                        options={[
                          { value: '1', label: '월' }, { value: '2', label: '화' }, { value: '3', label: '수' },
                          { value: '4', label: '목' }, { value: '5', label: '금' }, { value: '6', label: '토' },
                          { value: '0', label: '일' },
                        ]}
                        width={62}
                      />

                      {/* 시작시간 */}
                      <CommonSelect
                        value={splitTime(row.startTime).hour}
                        onChange={e => updateScheduleRow(index, 'startTime', joinTime(e.target.value, splitTime(row.startTime).minute))}
                        options={HOUR_OPTIONS}
                        width={62}
                      />
                      <Typography sx={{ fontSize: '13px', color: '#999' }}>:</Typography>
                      <CommonSelect
                        value={splitTime(row.startTime).minute}
                        onChange={e => updateScheduleRow(index, 'startTime', joinTime(splitTime(row.startTime).hour, e.target.value))}
                        options={MINUTE_OPTIONS}
                        width={62}
                      />

                      <Typography sx={{ mx: '4px', color: '#bbb' }}>~</Typography>

                      {/* 종료시간 */}
                      <CommonSelect
                        value={splitTime(row.endTime).hour}
                        onChange={e => updateScheduleRow(index, 'endTime', joinTime(e.target.value, splitTime(row.endTime).minute))}
                        options={HOUR_OPTIONS}
                        width={62}
                      />
                      <Typography sx={{ fontSize: '13px', color: '#999' }}>:</Typography>
                      <CommonSelect
                        value={splitTime(row.endTime).minute}
                        onChange={e => updateScheduleRow(index, 'endTime', joinTime(splitTime(row.endTime).hour, e.target.value))}
                        options={MINUTE_OPTIONS}
                        width={62}
                      />

                      {/* 강사 */}
                      <CommonSelect
                        value={row.trainerId || ''}
                        onChange={e => updateScheduleRow(index, 'trainerId', e.target.value)}
                        options={trainerOptions}
                        emptyLabel="강사"
                        width={120}
                      />
                      {/* 강의실 */}
                      {type !== 'ONLINE_LESSON' && (
                        <CommonSelect
                          value={row.roomId || ''}
                          onChange={e => updateScheduleRow(index, 'roomId', e.target.value)}
                          options={roomOptions}
                          emptyLabel="강의실"
                          width={130}
                        />
                      )}

                      {/* 공개/비공개 토글 */}
                      <Switch
                        checked={row.openYn === 'Y'}
                        onChange={e => {
                          if (autoSettings.openScheduledYn === 'Y') {
                            setOpenYnConfirm({ index, checked: e.target.checked });
                          } else {
                            updateScheduleRow(index, 'openYn', e.target.checked ? 'Y' : 'N');
                          }
                        }}
                        sx={{
                          ml: 'auto',
                          width: 36, height: 20, padding: 0,
                          '& .MuiSwitch-switchBase': {
                            padding: '2px',
                            '&.Mui-checked': {
                              transform: 'translateX(16px)', color: '#fff',
                              '& + .MuiSwitch-track': { backgroundColor: '#4AB3BC', opacity: 1 },
                            },
                          },
                          '& .MuiSwitch-thumb': { width: 16, height: 16, boxShadow: 'none' },
                          '& .MuiSwitch-track': { borderRadius: 10, backgroundColor: '#ccc', opacity: 1 },
                        }}
                      />
                      <Typography sx={{ fontSize: '11px', color: row.openYn === 'Y' ? '#4AB3BC' : '#999', minWidth: '40px' }}>
                        {row.openYn === 'Y' ? '수업공개' : '수업비공개'}
                      </Typography>

                      {/* 복제/삭제 (등록 모드에서만) */}
                      {!isEditMode && (
                        <Box sx={{ display: 'flex', gap: '6px' }}>
                          <ScheduleActionButton buttonVariant="add" onClick={() => addScheduleRow(index)}>
                            복제
                          </ScheduleActionButton>
                          <ScheduleActionButton buttonVariant="remove" onClick={() => removeScheduleRow(index)} disabled={scheduleRows.length <= 1}>
                            삭제
                          </ScheduleActionButton>
                        </Box>
                      )}
                    </ScheduleRow>
                  ))}
                </Box>
              ))}
            </ScheduleSection>
          </ScheduleCreateContent>

          <ModalFooter>
            <CommonModalButton variant="cancel" onClick={() => { if (!isEditMode) handleDateSelect?.(); handleClose(); }}>
              취소
            </CommonModalButton>
            <CommonModalButton variant="submit" onClick={handleFormSubmit} disabled={loader}>
              {loader ? (isEditMode ? '수정 중...' : '등록 중...') : (isEditMode ? '저장' : '일정등록')}
            </CommonModalButton>
          </ModalFooter>
        </ScheduleCreateContainer>
      </Modal>

      {/* 자동 공개&폐강 모달 */}
      {moveConfirm.element}

      <AutoPublishCloseModal
        open={autoSettingOpen}
        onClose={() => setAutoSettingOpen(false)}
        settings={autoSettings}
        maxDate={lessonData?.eventData?.classDate || lesson?.registrationStartDate}
        onSave={newSettings => {
          setAutoSettings(newSettings);
          if (newSettings.openScheduledYn === 'Y') {
            setScheduleRows(prev => prev.map(r => ({ ...r, openYn: 'N' })));
          } else {
            setScheduleRows(prev => prev.map(r => ({ ...r, openYn: 'Y' })));
          }
        }}
      />

      {/* 수강권 선택 모달 */}
      <TicketSelectModal
        open={ticketModalOpen}
        onClose={() => setTicketModalOpen(false)}
        tickets={tickets}
        selectedIds={selectedTicketIds}
        onChangeIds={handleTicketIdsChange}
      />

      {/* 그룹수업 선택/변경 */}
      <TemplateSelectModal
        open={templateSelectOpen}
        onClose={() => setTemplateSelectOpen(false)}
        templateList={templateList}
        selectedTemplateId={selectedTemplateId}
        onSelect={handleTemplateSelect}
      />

      {/* 그룹수업 변경 확인 */}
      <Dialog open={templateConfirmOpen} onClose={handleTemplateCancelConfirm} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '16px', fontWeight: 600 }}>그룹수업 변경</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '14px', color: '#333', mb: '8px' }}>
            그룹수업을 <b style={{ color: '#4AB3BC' }}>{selectedTemplateName}</b>(으)로 변경하시겠습니까?
          </Typography>
          <Typography sx={{ fontSize: '13px', color: '#888' }}>
            수업명, 정원, 대기정원, 차감횟수, 수강권이 해당 그룹수업의 설정값으로 변경됩니다.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: '12px 24px' }}>
          <CommonModalButton variant="cancel" onClick={handleTemplateCancelConfirm}>취소</CommonModalButton>
          <CommonModalButton variant="submit" onClick={handleTemplateConfirm}>변경하기</CommonModalButton>
        </DialogActions>
      </Dialog>

      {/* 자동공개 설정 중 공개 토글 변경 시도 */}
      <Dialog open={!!openYnConfirm} onClose={() => setOpenYnConfirm(null)} maxWidth="xs" fullWidth>
        <DialogContent sx={{ p: '24px' }}>
          <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#333' }}>
            자동공개가 설정되어 있습니다
          </Typography>
          <Typography sx={{ fontSize: '13px', color: '#888', mt: '8px' }}>
            공개 상태를 수동으로 변경하려면 자동 공개 설정을 먼저 해제해주세요.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: '12px 24px' }}>
          <CommonModalButton variant="cancel" onClick={() => setOpenYnConfirm(null)}>닫기</CommonModalButton>
          <CommonModalButton variant="submit" onClick={() => {
            setOpenYnConfirm(null);
            setAutoSettingOpen(true);
          }}>자동공개 설정으로 이동</CommonModalButton>
        </DialogActions>
      </Dialog>

      {/* 확인 다이얼로그 */}
      <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)} sx={{ '& :focus-visible': { outline: 'none' } }}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: '560px', backgroundColor: '#fff', borderRadius: '10px', overflow: 'hidden',
        }}>
          <ModalHeader>
            <ModalHeaderTitle>{isEditMode ? '수업 수정 확인' : '일정 등록 확인'}</ModalHeaderTitle>
            <CloseIcon onClick={() => setConfirmOpen(false)} sx={{ color: '#fff', cursor: 'pointer', '&:hover': { opacity: 0.7 } }} />
          </ModalHeader>

          <Box sx={{ p: '24px', maxHeight: '60vh', overflowY: 'auto' }}>
            {/* 현재일정만 수정 안내 (edit only) */}
            {isEditMode && (
              <Box sx={{ mb: '16px', pb: '16px', borderBottom: '1px solid #eee' }}>
                <Typography sx={{ fontSize: '13px', color: '#666' }}>
                  현재 선택한 일정만 수정됩니다.
                </Typography>
              </Box>
            )}

            {/* Summary table */}
            <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden', mb: '20px' }}>
            <Box component="table" sx={{
              width: '100%', borderCollapse: 'collapse',
              '& th': { fontSize: '13px', fontWeight: 600, color: '#555', textAlign: 'left', padding: '10px 14px', backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0', borderRight: '1px solid #e0e0e0', width: '100px' },
              '& td': { fontSize: '13px', color: '#333', padding: '10px 14px', borderBottom: '1px solid #e0e0e0' },
              '& tr:last-child th, & tr:last-child td': { borderBottom: 'none' },
            }}>
              <tbody>
                <tr><th>수업명</th><td>{watch('className')}</td></tr>
                <tr><th>수업 기간</th><td>{periodStart ? moment(periodStart).format('YYYY-MM-DD') : ''} ~ {periodEnd ? moment(periodEnd).format('YYYY-MM-DD') : ''}</td></tr>
                {type === 'GROUP_LESSON' && (
                  <>
                    <tr><th>수강권</th><td>{selectedTicketCount}개 선택됨</td></tr>
                    <tr><th>차감 횟수</th><td>{watch('courseTicketDeductionCount') || '-'}회</td></tr>
                    <tr><th>정원</th><td>{(isEditMode ? watch('membersMaxCount') : studentsCounts) || '-'}명 / 대기 {(isEditMode ? watch('membersWaitMaxCount') : waitStudentsCounts) || '0'}명</td></tr>
                  </>
                )}
                {autoSettings.openScheduledYn === 'Y' && (
                  <tr><th>자동 공개</th><td>{formatOpenSchedule(autoSettings)}</td></tr>
                )}
                {autoSettings.closeScheduledYn === 'Y' && (
                  <tr><th>자동 폐강</th><td>수업 시작 {autoSettings.closeHour || '-'}시간 전, {autoSettings.closeMinCount || '-'}명 이하시</td></tr>
                )}
              </tbody>
            </Box>
            </Box>

            {/* Schedule list */}
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#333', mb: '8px' }}>
              스케줄 ({scheduleRows.length}건)
            </Typography>
            <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
            <Box component="table" sx={{
              width: '100%', borderCollapse: 'collapse',
              '& th': { fontSize: '12px', fontWeight: 600, color: '#555', padding: '8px 12px', backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0', textAlign: 'center' },
              '& td': { fontSize: '13px', color: '#333', padding: '8px 12px', borderBottom: '1px solid #e0e0e0', textAlign: 'center' },
              '& tr:last-child td': { borderBottom: 'none' },
            }}>
              <thead>
                <tr><th>요일</th><th>시간</th><th>공개</th></tr>
              </thead>
              <tbody>
                {scheduleRows.map((row, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 600 }}>{DAY_NUMBER_TO_LABEL[row.dayOfWeek]?.replace('요일', '') || ''}</td>
                    <td>{row.startTime} ~ {row.endTime}</td>
                    <td style={{ color: row.openYn === 'Y' ? '#0F7F85' : '#999', fontWeight: 600 }}>
                      {row.openYn === 'Y' ? '수업공개' : '수업비공개'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Box>
            </Box>
          </Box>

          <ModalFooter>
            <CommonModalButton variant="cancel" onClick={() => setConfirmOpen(false)}>
              취소
            </CommonModalButton>
            <CommonModalButton variant="submit" onClick={handleConfirmSubmit} disabled={loader}>
              {loader ? (isEditMode ? '수정 중...' : '등록 중...') : (isEditMode ? '수정하기' : '등록하기')}
            </CommonModalButton>
          </ModalFooter>
        </Box>
      </Modal>

      {/* 예약자 경고 다이얼로그 (edit only) */}
      {isEditMode && (
        <Dialog open={!!reservationWarning} onClose={handleCancelReservationWarning} maxWidth="sm" fullWidth>
          <DialogTitle>{reservationWarning?.total != null ? '예약/대기 회원이 있는 수업입니다' : '예약자가 있을 수 있는 수업입니다'}</DialogTitle>
          <DialogContent>
            <ReservationWarningTitle>
              {reservationWarning?.total != null ? (
                <>예약/대기 회원 <b>{reservationWarning.total}명</b> 에게 변경 알림이 발송됩니다.</>
              ) : (
                <>예약/대기 회원에게 변경 알림이 발송될 수 있습니다.</>
              )}
            </ReservationWarningTitle>
            <ReservationWarningSub>
              진행하시면 변경 시간으로 저장되고 회원 앱에 푸시 알림이 즉시 발송됩니다.
            </ReservationWarningSub>
          </DialogContent>
          <DialogActions>
            <CommonModalButton variant="cancel" onClick={handleCancelReservationWarning}>
              취소
            </CommonModalButton>
            <CommonModalButton variant="submit" onClick={handleProceedDespiteReservation} disabled={loader}>
              진행
            </CommonModalButton>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
